import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Company() {
  useEffect(() => {
    // Update page title and meta description
    document.title = '会社概要 - おかやまリフォーム株式会社 | 岡山市・倉敷市のリフォーム専門業者';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '岡山市・倉敷市のリフォーム専門業者おかやまリフォーム株式会社の会社概要。2000年設立、地域密着25年の実績。代表取締役岡山太郎、従業員5名で安心施工をお約束します。');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', '会社概要,おかやまリフォーム,岡山市,倉敷市,リフォーム会社,企業情報');
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/company`);
    }

    // Schema.org JSON-LD for Organization
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "おかやまリフォーム株式会社",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/company`,
      "description": "岡山市・倉敷市を中心とした岡山県内のリフォーム・修繕工事・店舗工事専門業者",
      "foundingDate": "2000",
      "numberOfEmployees": "5",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "○○町1-2-3",
        "addressLocality": "岡山市北区",
        "addressRegion": "岡山県",
        "postalCode": "700-0000",
        "addressCountry": "JP"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "086-012-3456",
        "contactType": "customer service",
        "areaServed": "JP",
        "availableLanguage": "Japanese"
      },
      "founder": {
        "@type": "Person",
        "name": "岡山太郎"
      },
      "areaServed": ["岡山市", "倉敷市", "岡山県"]
    };

    const existingScript = document.getElementById('organization-schema');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'organization-schema';
    script.textContent = JSON.stringify(organizationSchema);
    document.head.appendChild(script);

    return () => {
      const schemaScript = document.getElementById('organization-schema');
      if (schemaScript) schemaScript.remove();
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative h-64 sm:h-80 md:h-96 bg-cover bg-center" style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/cd044f95f17aa908df0717ba9b779cd3.jpg')`
        }}>
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="relative z-10 h-full flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
                会社概要
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-white/90">
                岡山県で25年の実績を持つ<br className="sm:hidden" />リフォーム専門会社
              </p>
            </div>
          </div>
        </section>

        {/* Company Overview */}
        <section className="py-12 sm:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
                  <strong>おかやまリフォーム株式会社について</strong>
                </h2>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4 sm:mb-6">
                  2000年の設立以来、岡山市・倉敷市を中心とした岡山県内で、住宅リフォーム・修繕工事・店舗工事を手がけてまいりました。地域密着型の企業として、お客様一人ひとりのニーズに寄り添い、安心・安全な施工をお約束いたします。
                </p>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4 sm:mb-6">
                  経験豊富なスタッフが、設計から施工、アフターサービスまで一貫してサポート。岡山県内での豊富な実績と信頼を基に、お客様の理想の住環境づくりをお手伝いいたします。
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <a
                    href="tel:086-012-3456"
                    className="bg-teal-600 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold hover:bg-teal-700 transition-colors text-center cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    お電話でのご相談
                  </a>
                  <a
                    href="/#contact"
                    className="border border-teal-600 text-teal-600 px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold hover:bg-teal-50 transition-colors text-center cursor-pointer whitespace-nowrap"
                  >
                    お問い合わせフォーム
                  </a>
                </div>
              </div>
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20construction%20team%20working%20on%20renovation%20project%2C%20skilled%20workers%20in%20safety%20equipment%2C%20quality%20craftsmanship%2C%20modern%20tools%2C%20teamwork%2C%20construction%20site%2C%20experienced%20professionals&width=600&height=400&seq=company-team&orientation=landscape"
                  alt="施工チーム"
                  className="w-full h-auto rounded-lg shadow-lg object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Company Information */}
        <section className="py-12 sm:py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8 sm:mb-12">
              <strong>会社情報</strong>
            </h2>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="p-6 sm:p-8">
                  <table className="w-full">
                    <tbody>
                      <tr className="border-b border-gray-200">
                        <td className="py-3 sm:py-4 font-semibold text-gray-900 align-top text-sm sm:text-base" style={{width: '35%'}}>会社名</td>
                        <td className="py-3 sm:py-4 text-gray-700 text-sm sm:text-base">おかやまリフォーム株式会社</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="py-3 sm:py-4 font-semibold text-gray-900 align-top text-sm sm:text-base">代表取締役</td>
                        <td className="py-3 sm:py-4 text-gray-700 text-sm sm:text-base">岡山 太郎</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="py-3 sm:py-4 font-semibold text-gray-900 align-top text-sm sm:text-base">設立</td>
                        <td className="py-3 sm:py-4 text-gray-700 text-sm sm:text-base">2000年</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="py-3 sm:py-4 font-semibold text-gray-900 align-top text-sm sm:text-base">従業員数</td>
                        <td className="py-3 sm:py-4 text-gray-700 text-sm sm:text-base">5名</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="py-3 sm:py-4 font-semibold text-gray-900 align-top text-sm sm:text-base">所在地</td>
                        <td className="py-3 sm:py-4 text-gray-700 text-sm sm:text-base">〒700-0000<br />岡山市北区○○町1-2-3</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="py-3 sm:py-4 font-semibold text-gray-900 align-top text-sm sm:text-base">電話番号</td>
                        <td className="py-3 sm:py-4 text-gray-700 text-sm sm:text-base whitespace-nowrap">086-012-3456</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="py-3 sm:py-4 font-semibold text-gray-900 align-top text-sm sm:text-base">FAX番号</td>
                        <td className="py-3 sm:py-4 text-gray-700 text-sm sm:text-base whitespace-nowrap">086-012-3457</td>
                      </tr>
                      <tr>
                        <td className="py-3 sm:py-4 font-semibold text-gray-900 align-top text-sm sm:text-base">営業時間</td>
                        <td className="py-3 sm:py-4 text-gray-700 text-sm sm:text-base">9:00〜18:00<br />（日曜日・祝日定休）</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="p-6 sm:p-8 bg-gray-50">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">事業内容</h3>
                  <ul className="space-y-2 sm:space-y-3 text-sm sm:text-base">
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                      <span className="text-gray-700">住宅リフォーム工事</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                      <span className="text-gray-700">修繕工事</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                      <span className="text-gray-700">店舗工事</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                      <span className="text-gray-700">外壁・屋根工事</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                      <span className="text-gray-700">水回りリフォーム</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                      <span className="text-gray-700">内装工事</span>
                    </li>
                  </ul>

                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6 mt-6 sm:mt-8">資格・許可</h3>
                  <ul className="space-y-2 sm:space-y-3 text-sm sm:text-base">
                    <li className="flex items-start">
                      <i className="ri-award-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                      <span className="text-gray-700">建設業許可（般-○○）第○○○○○号</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-award-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                      <span className="text-gray-700">一級建築士事務所登録</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-award-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                      <span className="text-gray-700">岡山県建設業協会会員</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Access Map */}
        <section className="py-12 sm:py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8 sm:mb-12">
              <strong>アクセス</strong>
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">所在地</h3>
                <div className="bg-gray-50 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
                  <div className="space-y-2 sm:space-y-3 text-sm sm:text-base">
                    <p className="text-base sm:text-lg font-semibold text-gray-900">おかやまリフォーム株式会社</p>
                    <p className="text-gray-700">〒700-0000 岡山市北区○○町1-2-3</p>
                    <p className="text-gray-700">TEL: 086-012-3456</p>
                    <p className="text-gray-700">FAX: 086-012-3457</p>
                  </div>
                </div>
                <div className="space-y-3 sm:space-y-4">
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900">交通アクセス</h4>
                  <ul className="space-y-2 text-sm sm:text-base text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-train-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                      <span>JR岡山駅より車で15分</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-bus-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                      <span>岡山駅前バス停より○○行きバス「○○町」下車徒歩3分</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-car-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                      <span>山陽自動車道岡山ICより車で20分</span>
                    </li>
                  </ul>
                  <p className="text-xs sm:text-sm text-gray-600 mt-3 sm:mt-4">
                    ※駐車場完備（3台）お車でお越しの際はお気軽にご利用ください。
                  </p>
                </div>
              </div>
              <div>
                <div className="bg-gray-200 rounded-lg h-64 sm:h-80 md:h-96 flex items-center justify-center">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3281.8!2d133.9341!3d34.6617!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzTCsDM5JzQyLjEiTiAxMzPCsDU2JzAyLjgiRQ!5e0!3m2!1sja!2sjp!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0, borderRadius: '0.5rem' }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="おかやまリフォーム株式会社の所在地"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
